import React, { useState, useEffect, useRef } from 'react';
import { Itinerary, TripDetails } from '../../types/itinerary';
import { PlannerWizard } from './PlannerWizard';
import { generateItinerary, simulateGenerateItinerary } from '../../services/itinerary/builder';

interface ItineraryPanelProps {
  itinerary: Partial<Itinerary>;
  onLocationSelect: (locationId: string) => void;
  selectedLocationId?: string;
  onLocationsUpdate?: (locations: any[]) => void;
  streamingActivity?: boolean;
  streamingBudget?: boolean; // Add this
  activeDay?: number;
  onItineraryUpdate?: (itinerary: Partial<Itinerary>, streamingActivity?: boolean) => void;
  plannerWizardRef?: React.RefObject<any>;
}

interface EnrichedTripDetails extends TripDetails {
  isLoadingSavedItinerary?: boolean;
  savedItineraryId?: string;
}


export function ItineraryPanel({ 
  itinerary, 
  onLocationSelect,
  selectedLocationId,
  onLocationsUpdate,
  onItineraryUpdate,
  streamingBudget,
  plannerWizardRef
}: ItineraryPanelProps) {
  console.log("ItineraryPanel rendering with:", { 
    itinerary, 
    hasDestination: !!itinerary.tripDetails?.destination,
    days: itinerary.days?.length
  });
  
  const [streamingActivity, setStreamingActivity] = useState(false);
  const [activeDay, setActiveDay] = useState<number | undefined>();

  const [tripPlannerError, setTripPlannerError] = useState<string | null>(null);
  const [currentItinerary, setCurrentItinerary] = useState<Partial<Itinerary>>(itinerary);
  const [isGeneratingItinerary, setIsGeneratingItinerary] = useState(false);
  const pendingRequestIdRef = useRef<string | null>(null);
  const [forceShowDetails, setForceShowDetails] = useState(false);
  const shouldShowResults = !forceShowDetails && !!currentItinerary.tripDetails?.destination;
  const sentLocationsCountRef = useRef(0);


  // Configurable days per chunk setting
  const [daysPerChunk, setDaysPerChunk] = useState(2);

  // In ItineraryPanel_minimal.tsx, log when you receive the ref
  console.log('[ItineraryPanel_minimal] Received plannerWizardRef: ---------------------- ', plannerWizardRef);
  // Update local state when prop changes
  useEffect(() => {
    setCurrentItinerary(itinerary);
  }, [itinerary]);
  
  // Log updates to the itinerary
  useEffect(() => {
    console.log("Itinerary updated:", currentItinerary);
    console.log("Days in itinerary:", currentItinerary.days?.length);
  }, [currentItinerary]);
  

  // Add a callback to handle back navigation
  const handleBackToDetails = () => {
    setForceShowDetails(true);
    // Reset after a short delay to allow future auto-transitions
    setTimeout(() => {
      setForceShowDetails(false);
    }, 100);
  };


  const handleTripPlannerSubmit = async (details: TripDetails & { savedItineraryId?: string }) => {
    if (isGeneratingItinerary) {
      console.log("[ItineraryPanel] Already generating, ignoring duplicate submission");
      return;
    }
    
    // Generate a unique request ID
    const requestId = Date.now().toString();
    console.log("Trip planner submit with details:", details);
    
    setTripPlannerError(null);
    setIsGeneratingItinerary(true);
    pendingRequestIdRef.current = requestId;
    
    // Create initial structure
    const initialItinerary = {
      tripDetails: {
        destination: details.destination,
        startDate: details.startDate?.toISOString(),
        endDate: details.endDate?.toISOString(),
        travelGroup: details.travelGroup
      },
      days: []
    };
    
    setCurrentItinerary(initialItinerary);
    if (onItineraryUpdate) {
      onItineraryUpdate(initialItinerary, false);
    }
    
    // Common callback
    const handleItineraryUpdate = (partialItinerary, isStreaming, activeDay) => {
      // Only process updates for the current request
      if (pendingRequestIdRef.current !== requestId) {
        console.log(`[ItineraryPanel] Ignoring update from stale request ${requestId}`);
        return;
      }

      // Only set streaming false when ENTIRE generation is done
      if (isGeneratingItinerary) {
        setStreamingActivity(true); // Keep true during entire generation
      } else {
        setStreamingActivity(isStreaming || false);
      }

      // ADD THESE TWO LINES
      setStreamingActivity(isStreaming || false);
      setActiveDay(activeDay);

      setCurrentItinerary(partialItinerary);
      
      if (onItineraryUpdate) {
        onItineraryUpdate(partialItinerary, isStreaming);
      }
      
      if (onLocationsUpdate && partialItinerary.days) {
        const allLocations = partialItinerary.days.flatMap(day => 
          day.activities?.map(activity => ({
            ...activity.location,
            dayNumber: day.dayNumber  // Add day number to each location
          })) || []
        );
        
        if (allLocations.length > 0) {
          onLocationsUpdate(allLocations);
        }
        /* if (allLocations.length > 0) {
          onLocationsUpdate(allLocations);
          
          // For saved itineraries, check if ALL locations sent
          if (partialItinerary.id) {
            const totalExpectedActivities = partialItinerary.days.reduce((sum, day) => 
              sum + (day.activities?.length || 0), 0
            );
            
            if (allLocations.length === totalExpectedActivities) {
              console.log('[ItineraryPanel] All locations sent for saved itinerary');
              
              // Wait for markers to be created and initial bounds animation
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('itinerary-ready-for-selection'));
              }, 4000); // Bounds animation (3s) + marker creation buffer
            }
          }
          
          // For streaming, existing budget check
          if (partialItinerary.budgetSummary && !isStreaming) {
            window.dispatchEvent(new CustomEvent('itinerary-ready-for-selection'));
          }
        } */
      }
    };
    
    try {
      if (details.isLoadingSavedItinerary && details.savedItineraryId) {
        console.log("[ItineraryPanel] Loading saved itinerary");
        // Use simulation for saved itineraries
        await simulateGenerateItinerary(details, handleItineraryUpdate);
      } else {
        console.log("[ItineraryPanel] Generating new itinerary with daysPerChunk:", daysPerChunk);
        // Use real API for new itineraries with configurable chunk size
        await generateItinerary(
          details,
          handleItineraryUpdate,
          daysPerChunk // Pass the configurable chunk size
        );
      }
      console.log("[ItineraryPanel] Itinerary generation complete");
    } catch (error) {
      console.error('[ItineraryPanel] Error generating/loading itinerary:', error);
      setTripPlannerError(details.isLoadingSavedItinerary ? 
        'Failed to load saved itinerary.' : 
        'Failed to generate itinerary. Please try again.');
    } finally {
      console.log("[ItineraryPanel] Finalizing request:", requestId);
      setIsGeneratingItinerary(false);
      pendingRequestIdRef.current = null;
    }
  };

  return (
    <div style={{
      position: 'relative', 
      height: '100%',
      width: 'var(--itinerary-panel-width, 500px)',
      maxWidth: 'var(--itinerary-panel-max-width, 100%)'
      //padding: 'var(--itinerary-panel-padding, 1rem)'
    }}>
      <PlannerWizard 
        ref={plannerWizardRef} // Use the passed prop directly
        onSubmit={handleTripPlannerSubmit}
        isLoading={isGeneratingItinerary}
        error={tripPlannerError}
        itinerary={currentItinerary}
        onLocationSelect={onLocationSelect}
        selectedLocationId={selectedLocationId}
        streamingActivity={streamingActivity}
        activeDay={activeDay}  // This should be a number (1-4) during building
        onItineraryUpdate={onItineraryUpdate}
        onLocationsUpdate={onLocationsUpdate}
        isGeneratingItinerary={isGeneratingItinerary}
        //shouldShowResults={!!currentItinerary.tripDetails?.destination}
        shouldShowResults={shouldShowResults} // This will now be false when forceShowDetails is true
        onBackToDetails={handleBackToDetails} // New prop
      />
    </div>
  );
}